import { EpicIcons, TooltipPosition } from "@constellation-academy/epic-ui";
import { useContext, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import { type deleteCourseLearnableButton_DeleteTreeMutation } from "@relay/deleteCourseLearnableButton_DeleteTreeMutation.graphql";
import { type deleteCourseLearnableButton_UnpublishedLearnableFragment$key } from "@relay/deleteCourseLearnableButton_UnpublishedLearnableFragment.graphql";
import {
	DELETE_MUTATION,
	UNPUBLISHED_LEARNABLE_FRAGMENT,
} from "./delete-course-learnable-button.graphql";
import { type DeleteCourseLearnableButtonProps } from "./delete-course-learnable-button.types";
import { Button, ButtonVariant } from "../../../../components/button";
import { ConfirmWithTextDialog } from "../../../../components/confirm-with-text-dialog";
import { OffersScreenContext } from "../../offers.context";

export const DeleteCourseLearnableButtonComponent = ({
	learnableFragmentRef,
	disabled,
}: DeleteCourseLearnableButtonProps) => {
	const { t } = useTranslation("common");
	const learnable = useFragment<deleteCourseLearnableButton_UnpublishedLearnableFragment$key>(
		UNPUBLISHED_LEARNABLE_FRAGMENT,
		learnableFragmentRef,
	);
	const { connectionId } = useContext(OffersScreenContext);
	const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
	const [deleteCourseLearnable] =
		useMutation<deleteCourseLearnableButton_DeleteTreeMutation>(DELETE_MUTATION);

	const handleOnClick = () => {
		deleteCourseLearnable({
			variables: {
				input: {
					rootId: learnable?.root?.id!,
				},
				connections: [connectionId],
			},
			onCompleted: () => {
				setShowConfirmDeleteDialog(false);
				toast.success(t("offers.deleteSuccess"));
			},
		});
	};

	return (
		<>
			{showConfirmDeleteDialog && (
				<ConfirmWithTextDialog
					title={t("offers.deleteTitle", {
						title: learnable.root?.structureDefinition.title,
					})}
					content={
						<>
							<div>
								{t("offers.deleteContent", {
									title: learnable.root?.structureDefinition.title,
								})}
							</div>
							<div className="mt-4">{t("offers.deleteInstruction")}</div>
						</>
					}
					onCancel={() => {
						setShowConfirmDeleteDialog(false);
					}}
					onConfirm={handleOnClick}
					textToConfirm={learnable.root?.structureDefinition.title!}
				/>
			)}
			<Button
				disabled={disabled}
				onClick={() => {
					setShowConfirmDeleteDialog(true);
				}}
				icon={EpicIcons.TRASH}
				variant={ButtonVariant.Error}
				position={TooltipPosition.Left}
				tooltip={t("offers.delete")}
			/>
		</>
	);
};
